/**
 * Routes of area (b) — clients. OWNED BY area (b) — clients (paths kept: boards.ts hrefs point at them).
 * Search params are UI state: `?fenetre=` opens a window, `?phase=` focuses a phase of the onboarding sheet,
 * `?filtre=` / `?statut=` keep a list filter in the address.
 */
import { lazy, Suspense } from "react";
import { createRoute } from "@tanstack/react-router";
import { z } from "zod";
import { guard, staffRoute } from "@/app/route-base";
import { PageSkeleton } from "@/ui/page";
import { FENETRES } from "./onboarding/fenetres";

const lazyPage = (load: () => Promise<{ default: React.ComponentType }>) => {
  const C = lazy(load);
  return function Page() {
    return (
      <Suspense fallback={<PageSkeleton />}>
        <C />
      </Suspense>
    );
  };
};

const fenetre = z.enum(FENETRES).optional().catch(undefined);

/** Admin-Dashboard */
export const comptaRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/compta",
  validateSearch: z.object({}),
  beforeLoad: guard("cabinet.dashboard", "read"),
  component: lazyPage(() => import("./boards/Admin-Dashboard")),
});

/** Admin-Clients */
export const comptaClientsRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/compta/clients",
  validateSearch: z.object({}),
  beforeLoad: guard("cabinet.clients", "read"),
  component: lazyPage(() => import("./boards/Admin-Clients")),
});

/** Admin-Client-Fiche */
export const comptaClientsCompanyidRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/compta/clients/$companyId",
  validateSearch: z.object({}),
  beforeLoad: guard("cabinet.clients", "read"),
  component: lazyPage(() => import("./boards/Admin-Client-Fiche")),
});

/** Admin-Onboardings (+ its quick windows: `?fenetre=…&dossier=<companyId>`). */
export const comptaOnboardingsRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/compta/onboardings",
  validateSearch: z.object({
    filtre: z.enum(["cabinet", "client"]).optional().catch(undefined),
    fenetre,
    dossier: z.string().optional().catch(undefined),
  }),
  beforeLoad: guard("cabinet.clients", "read"),
  component: lazyPage(() => import("./boards/Admin-Onboardings")),
});

/** Admin-Onboarding-Dossier (phases 1–2), Admin-Onboarding-Etapes (`?phase=3`: phases 3–4) — one sheet. */
export const comptaOnboardingsCompanyidRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/compta/onboardings/$companyId",
  validateSearch: z.object({
    phase: z.coerce.number().int().min(1).max(4).optional().catch(undefined),
    fenetre,
  }),
  beforeLoad: guard("cabinet.clients", "read"),
  component: lazyPage(() => import("./boards/Admin-Onboarding-Dossier")),
});

/** Admin-Prospects */
export const comptaProspectsRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/compta/prospects",
  validateSearch: z.object({ statut: z.enum(["new", "in_progress", "done"]).optional().catch(undefined) }),
  beforeLoad: guard("cabinet.prospects", "read"),
  component: lazyPage(() => import("./boards/Admin-Prospects")),
});

/** Admin-Prospect-Fiche */
export const comptaProspectsProspectidRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/compta/prospects/$prospectId",
  validateSearch: z.object({ fenetre: z.enum(["statut", "convertir", "sans-suite", "supprimer"]).optional().catch(undefined) }),
  beforeLoad: guard("cabinet.prospects", "read"),
  component: lazyPage(() => import("./boards/Admin-Prospect-Fiche")),
});

export const clientsRoutes = [
  comptaRoute,
  comptaClientsRoute,
  comptaClientsCompanyidRoute,
  comptaOnboardingsRoute,
  comptaOnboardingsCompanyidRoute,
  comptaProspectsRoute,
  comptaProspectsProspectidRoute,
] as const;
